<template>
    <!-- Form for editing details -->
    <div class="d-flex flex-column gap-3">
        <form @submit.prevent="postOption">
            <!-- Option -->
            <Field
                class="mt-0"
                :label="resolvedOptionLabel"
                v-model="formData.name"
                type="text"
                :required="true"
            />

            <AddressField
                v-if="endpoint == 'company-site'"
                :required="true"
                v-model="formData"
            />

            <Field
                v-if="endpoint == 'company-site'"
                :label="$t('audits.panels.options.phone')"
                v-model="formData.phone"
                type="tel"
                :params="{ placeholder: '0 (000) 000-0000' }"
            />

            <Field
                v-if="endpoint == 'company-site'"
                :label="
                    $t('audits.panels.options.lead', {
                        label: infectionPreventionLabel,
                    })
                "
                v-model="formData.ipac_lead_id"
                type="select"
                :params="{
                    options: employeeOptions,
                    placeholder: $t('audits.panels.options.leadPlaceholder', {
                        label: infectionPreventionLabel,
                    }),
                    searchable: true,
                }"
            />

            <Field
                v-if="endpoint == 'company-site'"
                :label="$t('audits.panels.options.bookingContact')"
                v-model="formData.booking_contact_id"
                type="select"
                :params="{
                    options: employeeOptions,
                    placeholder: $t(
                        'audits.panels.options.bookingContactPlaceholder'
                    ),
                    searchable: true,
                }"
            />

            <!-- Save Button -->
            <div
                style="
                    padding-top: var(--space-6);
                    border-top: 1px solid var(--neutral-600);
                "
            >
                <button type="submit" class="btn btn-secondary w-100">
                    {{ $t('audits.panels.actions.submit') }}
                </button>
            </div>
        </form>
        <button
            v-if="includeAllOptionsButton"
            class="btn btn-primary"
            @click="this.$emit('allOptions')"
            style="width: fit-content"
        >
            <i class="fi fi-chevron-left"></i>
            {{
                $t('audits.panels.options.allOptions', {
                    label: resolvedOptionPluralLabel,
                })
            }}
        </button>
    </div>
</template>

<script>
import Field from '../Field.vue';
import AddressField from '../fields/AddressField.vue';

export default {
    name: 'OptionForm',
    components: {
        Field,
        AddressField,
    },
    props: {
        option: {
            type: Object,
            default: () => ({}),
        },
        optionTitle: {
            type: String,
            required: true,
        },
        optionLabel: {
            type: String,
            default: null,
        },
        optionPluralLabel: {
            type: String,
            default: null,
        },
        endpoint: {
            type: String,
            required: true,
        },
        newEntry: {
            type: Boolean,
            default: false,
        },
        includeAllOptionsButton: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            formData: {},
        };
    },
    emits: ['submit', 'allOptions'],
    computed: {
        capitalizedOptionType() {
            return (
                this.optionTitle.charAt(0).toUpperCase() +
                this.optionTitle.slice(1)
            );
        },
        resolvedOptionLabel() {
            return this.optionLabel || this.capitalizedOptionType;
        },
        resolvedOptionPluralLabel() {
            return this.optionPluralLabel || `${this.capitalizedOptionType}s`;
        },
        employeeOptions() {
            if (
                !this.newEntry &&
                this.endpoint === 'company-site' &&
                this.formData?.id
            ) {
                return this.$employeesStore.getEmployeeOptionsBySites([
                    this.formData.id,
                ]);
            }

            return this.$employeesStore.getEmployeeOptions();
        },
    },
    watch: {
        employeeOptions: {
            immediate: true,
            handler(newOptions) {
                const optionIds = new Set(
                    (newOptions || []).map((option) => String(option.value))
                );

                if (
                    this.formData?.ipac_lead_id &&
                    !optionIds.has(String(this.formData.ipac_lead_id))
                ) {
                    this.formData.ipac_lead_id = null;
                }

                if (
                    this.formData?.booking_contact_id &&
                    !optionIds.has(String(this.formData.booking_contact_id))
                ) {
                    this.formData.booking_contact_id = null;
                }
            },
        },
    },
    methods: {
        resetForm() {
            this.formData = {
                id: this.option?.id || '',
                name: this.option?.name || '',
                address: this.option?.address || '',
                city: this.option?.city || '',
                province: this.option?.province || '',
                country: this.option?.country || '',
                phone: this.option?.phone || '',
                ipac_lead_id: this.option?.ipac_lead_id || null,
                booking_contact_id: this.option?.booking_contact_id || null,
            };
        },
        postOption() {
            const action = this.newEntry ? 'create' : 'update';
            const url = `${this.endpoint}/${action}`;

            let formData = { ...this.formData };

            if (action == 'create') {
                const { id, ...rest } = formData;
                formData = rest;
            }

            this.$postData(url, formData).then((response) => {
                if (response?.success) {
                    this.$emit('submit', response.data);
                    if (this.newEntry) {
                        this.$alertStore.pushSuccess(
                            this.$t('audits.panels.options.created', {
                                label: this.resolvedOptionLabel,
                            })
                        );
                    }
                } else {
                    this.$alertStore.pushError(response?.message);
                }
            });
        },
    },
    mounted() {
        this.resetForm();
    },
};
</script>

<style scoped>
.form-group {
    margin: 0.85rem 0;
}
</style>
